import { readFileSync } from "node:fs";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import type { InstalledStamp } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { CLI_VERSION } from "../../lib/package-version.js";
import { readStamp, writeStamp } from "../../lib/platform-stamp.js";
import { discoverStampStorage } from "../../lib/platform-storage-discovery.js";
import { buildSeedStamp } from "../../lib/platform-stamp-seed.js";
import { validateManifest, type ReleaseManifest } from "../../lib/release-manifest.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export interface SeedStampArgs {
  /**
   * Path to the fetched, digest-verified release manifest — the ONE record of
   * what this install deployed, and the same document every later converge
   * stamps from.
   */
  manifestPath: string;
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  nowIso?: string;
  readFileImpl?: (p: string) => string;
  readStampImpl?: (o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }) => Promise<InstalledStamp | null>;
  writeStampImpl?: (s: InstalledStamp, o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string; onProgress?: (m: string) => void }) => Promise<void>;
  discoverImpl?: (o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }) => Promise<{ tableEndpoint: string; accountResourceId: string; accountName: string }>;
  onProgress?: (m: string) => void;
}

export interface SeedStampResult {
  // "seeded-partial" is gone with the descriptor. It meant "the image could not
  // see its own tree, so this stamp records images but no content" — a state that
  // cannot occur now: the descriptor generator fails the build rather than emit a
  // contentless one, and this seeds from the manifest, which always names content.
  outcome: "seeded" | "already-stamped";
  platformVersion: string;
  storageAccount: string;
  components?: Record<string, string>;
}

/**
 * Seed the installed stamp from the release manifest — ONLY IF ABSENT.
 *
 * The only-if-absent guard is the whole idempotency story: a re-run, an installer
 * container restart, or a second call after a real converge must never clobber a stamp
 * that another writer owns. The converge engine remains the sole authority for updates.
 *
 * There is no `--platform-version` override any more, and no validation of one.
 * The version comes from the manifest, which the engine has already proven
 * declares the release it was told to install (manifest_declares_version in
 * installer/lib.sh). A second value could only ever disagree with a fact already
 * established, and the stamp — what the updater diffs against and what fleet
 * telemetry aggregates — is the worst possible place to find that out.
 */
export async function seedStamp(args: SeedStampArgs): Promise<SeedStampResult> {
  const readFileImpl = args.readFileImpl ?? ((p: string) => readFileSync(p, "utf8"));
  const readStampImpl = args.readStampImpl ?? readStamp;
  const writeStampImpl =
    args.writeStampImpl ??
    ((stamp: InstalledStamp, o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string; onProgress?: (m: string) => void }) =>
      writeStamp({ ...o, stamp }));
  const discoverImpl = args.discoverImpl ?? discoverStampStorage;
  const ctx = { credential: args.credential, subscriptionId: args.subscriptionId, resourceGroup: args.resourceGroup };

  let raw: unknown;
  try {
    raw = JSON.parse(readFileImpl(args.manifestPath));
  } catch (e) {
    throw new LocalCliError({
      code: "PLATFORM_SEED_MANIFEST_UNREADABLE",
      message: `Cannot read the release manifest at ${args.manifestPath}.`,
      hint: "The installer writes it at preflight after verifying its digest. Reaching this without one means the manifest gate was bypassed.",
      cause: e,
    });
  }
  const errors = validateManifest(raw);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_SEED_MANIFEST_INVALID",
      message: `The release manifest at ${args.manifestPath} is not valid: ${errors.join("; ")}.`,
      hint: "This install was launched against a manifest the platform cannot read. Nothing was stamped.",
    });
  }
  const manifest = raw as ReleaseManifest;

  const { accountName } = await discoverImpl(ctx);
  const existing = await readStampImpl(ctx);
  if (existing) {
    args.onProgress?.(`platform stamp already present (${existing.platformVersion}) — leaving it untouched.`);
    return { outcome: "already-stamped", platformVersion: existing.platformVersion, storageAccount: accountName };
  }

  const stamp = buildSeedStamp(manifest, CLI_VERSION, args.nowIso ?? new Date().toISOString());
  await writeStampImpl(stamp, { ...ctx, onProgress: args.onProgress });

  return {
    outcome: "seeded",
    platformVersion: stamp.platformVersion,
    storageAccount: accountName,
    components: {
      gateway: stamp.components.gateway.tag,
      codingAgent: stamp.components.codingAgent.tag,
      azureExecutor: stamp.components.azureExecutor.tag,
    },
  };
}

export class PlatformSeedStampCommand extends M8tCommand {
  static paths = [["platform", "seed-stamp"]];
  static usage = Command.Usage({
    description: "INTERNAL: write the initial installed-state stamp from the release manifest this install deployed.",
    details:
      "Used by the cloud installer at the end of a from-zero install so the platform records which release it is running. Seeds from the SAME document every later converge stamps from, so the first update diffs against a row that describes the same release. Writes the stamp ONLY IF ABSENT — it never overwrites a stamp written by the converge engine, so a re-run or a container restart is harmless. Not a founder-facing command: to change what is installed, use 'm8t platform update'.",
  });

  manifest = Option.String("--manifest", { description: "Path to the fetched, digest-verified release manifest for the release this install deployed." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  miClientId = Option.String("--mi-client-id", { description: "Authenticate as this user-assigned managed identity instead of the ambient credential." });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_SEED_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };
    // Progress goes to STDERR so `--output json` keeps a clean, parseable stdout.
    const onProgress = (m: string) => { this.context.stderr.write(`${m}\n`); };

    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();

    const result = await seedStamp({
      manifestPath: need(this.manifest, "--manifest"),
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(renderJson(result) + "\n");
    } else {
      this.context.stdout.write(`${result.outcome}: ${result.platformVersion} (storage ${result.storageAccount})\n`);
    }
    return 0;
  }
}
